import { BoundingBox } from '../hooks/useSimulatedInspection';
import { Severity } from '../services/localBackend';
import { DetectedRegion } from './imageProcessing';

// ─── Legacy: draw simulated bounding boxes ───────────────────
export function drawBoundingBoxes(
  canvas: HTMLCanvasElement,
  image: HTMLImageElement | HTMLCanvasElement,
  boundingBoxes: BoundingBox[],
  severity: Severity
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  if (boundingBoxes.length === 0) return;

  const scaleX = canvas.width / (image instanceof HTMLImageElement ? image.naturalWidth : image.width);
  const scaleY = canvas.height / (image instanceof HTMLImageElement ? image.naturalHeight : image.height);

  boundingBoxes.forEach((box) => {
    const x = box.x * scaleX;
    const y = box.y * scaleY;
    const w = box.width * scaleX;
    const h = box.height * scaleY;

    let boxColor: string;
    let glowColor: string;
    if (severity === Severity.majorDefect) {
      boxColor = '#ef4444';
      glowColor = 'rgba(239, 68, 68, 0.3)';
    } else if (severity === Severity.minorDefect) {
      boxColor = '#f59e0b';
      glowColor = 'rgba(245, 158, 11, 0.3)';
    } else {
      boxColor = '#22c55e';
      glowColor = 'rgba(34, 197, 94, 0.3)';
    }

    ctx.shadowColor = boxColor;
    ctx.shadowBlur = 8;
    ctx.strokeStyle = boxColor;
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, w, h);

    const cornerLen = Math.min(w, h) * 0.2;
    ctx.lineWidth = 3;
    ctx.strokeStyle = boxColor;

    ctx.beginPath();
    ctx.moveTo(x, y + cornerLen);
    ctx.lineTo(x, y);
    ctx.lineTo(x + cornerLen, y);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x + w - cornerLen, y);
    ctx.lineTo(x + w, y);
    ctx.lineTo(x + w, y + cornerLen);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x, y + h - cornerLen);
    ctx.lineTo(x, y + h);
    ctx.lineTo(x + cornerLen, y + h);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x + w - cornerLen, y + h);
    ctx.lineTo(x + w, y + h);
    ctx.lineTo(x + w, y + h - cornerLen);
    ctx.stroke();

    ctx.shadowBlur = 0;

    const labelText = `${box.label} ${box.confidence}%`;
    ctx.font = 'bold 11px JetBrains Mono, monospace';
    const textWidth = ctx.measureText(labelText).width;
    const labelPadding = 4;
    const labelHeight = 18;
    const labelY = y > labelHeight + 4 ? y - labelHeight - 2 : y + 2;

    ctx.fillStyle = glowColor;
    ctx.fillRect(x, labelY, textWidth + labelPadding * 2, labelHeight);

    ctx.strokeStyle = boxColor;
    ctx.lineWidth = 1;
    ctx.strokeRect(x, labelY, textWidth + labelPadding * 2, labelHeight);

    ctx.fillStyle = boxColor;
    ctx.fillText(labelText, x + labelPadding, labelY + 13);
  });
}

// ─── New: draw labeled bounding box for real detection ───────
export function drawLabeledBoundingBox(
  ctx: CanvasRenderingContext2D,
  box: { x: number; y: number; width: number; height: number },
  label: string,
  color: string = '#ef4444'
): void {
  const { x, y, width: w, height: h } = box;

  // Glow shadow
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;

  // Main rectangle
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.strokeRect(x, y, w, h);

  // Corner accents
  const cornerLen = Math.min(w, h) * 0.18;
  ctx.lineWidth = 3;

  // Top-left
  ctx.beginPath();
  ctx.moveTo(x, y + cornerLen);
  ctx.lineTo(x, y);
  ctx.lineTo(x + cornerLen, y);
  ctx.stroke();

  // Top-right
  ctx.beginPath();
  ctx.moveTo(x + w - cornerLen, y);
  ctx.lineTo(x + w, y);
  ctx.lineTo(x + w, y + cornerLen);
  ctx.stroke();

  // Bottom-left
  ctx.beginPath();
  ctx.moveTo(x, y + h - cornerLen);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x + cornerLen, y + h);
  ctx.stroke();

  // Bottom-right
  ctx.beginPath();
  ctx.moveTo(x + w - cornerLen, y + h);
  ctx.lineTo(x + w, y + h);
  ctx.lineTo(x + w, y + h - cornerLen);
  ctx.stroke();

  ctx.shadowBlur = 0;

  // Label
  const fontSize = Math.max(10, Math.min(14, Math.floor(w / 8)));
  ctx.font = `bold ${fontSize}px JetBrains Mono, monospace`;
  const textWidth = ctx.measureText(label).width;
  const labelPadding = 5;
  const labelHeight = fontSize + 8;
  const labelY = y > labelHeight + 4 ? y - labelHeight - 2 : y + 2;
  const labelX = x;

  // Label background
  ctx.fillStyle = 'rgba(239, 68, 68, 0.85)';
  ctx.fillRect(labelX, labelY, textWidth + labelPadding * 2, labelHeight);

  // Label border
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.strokeRect(labelX, labelY, textWidth + labelPadding * 2, labelHeight);

  // Label text
  ctx.fillStyle = '#ffffff';
  ctx.fillText(label, labelX + labelPadding, labelY + labelHeight - 4);
}

// ─── Draw all detected regions on a canvas ───────────────────
export function drawDetectedRegions(
  canvas: HTMLCanvasElement,
  sourceImageData: ImageData,
  regions: DetectedRegion[]
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  canvas.width = sourceImageData.width;
  canvas.height = sourceImageData.height;

  // Draw the original (contrast-enhanced) image
  ctx.putImageData(sourceImageData, 0, 0);

  if (regions.length === 0) return;

  // Color per defect type
  const defectColors: Record<string, string> = {
    'Crack': '#ef4444',
    'Surface Wear': '#f59e0b',
    'Pitting': '#f97316',
    'Tooth Damage': '#ef4444',
  };

  for (const region of regions) {
    const color = defectColors[region.defectType] || '#ef4444';
    const confidence = 'confidence' in region ? (region as any).confidence : null;
    const label = confidence != null
      ? `${region.defectType} ${confidence}%`
      : region.defectType;
    drawLabeledBoundingBox(ctx, region.boundingBox, label, color);
  }
}

export function captureCanvasAsDataURL(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL('image/jpeg', 0.85);
}

export async function dataURLToUint8Array(dataURL: string): Promise<Uint8Array> {
  const response = await fetch(dataURL);
  const buffer = await response.arrayBuffer();
  return new Uint8Array(buffer);
}
